<template>
	<div>
		<bread-crumbs
			:title="$t('Follower.FollowersConfiguration')"
			:subTitle="$t('Follower.FollowerConfigurationText')"
			:sizeTitle="30"
			:lineHeightTitle="37"
			:sizeSubTitle="15"
			:lineHeightSubTitle="18"
		/>
		<FollowerFormEvent :event="event" class="mt-2" @select="handleSelectedEvent" />

		<FollowerFormChanel :follower="follower" @update="updateChanel" />

		<vx-card :title="$t('Follower.RecipientSettings')" class="mt-2">
			<p class="mb-2">{{ $t("Follower.RecipientSettingsText") }}.</p>

			<FollowerFormEmail
				v-if="follower.to_email"
				:emails="follower.email"
				@add="addEmail"
				@remove="removeEmail"
				class="mt-base"
			/>

			<FollowerFormPhone
				v-if="follower.whatsapp || follower.sms"
				:phones="follower.cell_phone"
				@add="addPhone"
				@remove="removePhone"
				class="mt-2"
			/>

			<vs-alert icon-pack="feather" icon="icon-info" color="warning" :active="activeAlert" class="mt-4">
				{{ $t("Follower.RecipientSettingsTextAlertText") }}
			</vs-alert>
		</vx-card>

		<FollowerFormTemplates
			v-if="event.id && templateList.length > 0"
			class="mt-2"
			:follower="follower"
			:event="event"
			:templateError="templateError"
			@templateSelected="handleSelectedTemplate"
			@languageSelected="handleSelectedLanguage"
		/>

		<vx-card class="mt-2">
			<div class="flex justify-end flex-wrap mt-4 gap-2">
				<vs-button icon-pack="feather" icon="icon-x" @click="handleCancel">{{ $t("Cancel") }}</vs-button>
				<vs-button icon-pack="feather" icon="icon-save" @click="handleSubmit">{{ $t("Save") }}</vs-button>
			</div>
		</vx-card>
	</div>
</template>

<script>
import Follower from "@/models/Follower";
import Event from "@/models/Event";

import vSelect from "vue-select";
import FollowerFormChanel from "./FollowerFormChanel.vue";
import FollowerFormEmail from "./FollowerFormEmail.vue";
import FollowerFormPhone from "./FollowerFormPhone.vue";
import FollowerFormEvent from "./FollowerFormEvent.vue";
import FollowerFormTemplates from "./FollowerFormTemplates.vue";

import moduleSchedule from "@/store/schedule/moduleSchedule.js";

import DateUtils from "@/helpers/DateUtils.js";

export default {
	name: "FollowerForm",
	components: {
		// component components
		vSelect,
		FollowerFormChanel,
		FollowerFormEmail,
		FollowerFormPhone,
		FollowerFormEvent,
		FollowerFormTemplates,
	},
	data() {
		return {
			// component data
			follower: new Follower({}),
			event: new Event({}),
			templateError: false,
			currentDay: new Date(),
			popupActive: false,
			lang: "",
			templateName: "",
		};
	},
	methods: {
		// component methods
		openLoading() {
			this.$vs.loading();
		},
		closeLoading() {
			this.$vs.loading.close();
		},
		addEmail(email) {
			this.follower.addEmail(email);
		},
		removeEmail(index) {
			this.follower.removeEmail(index);
		},
		addPhone(phone, prefix) {
			this.follower.addCellPhone(phone, prefix);
		},

		removePhone(index) {
			this.follower.removeCellPhone(index);
		},

		updateChanel(key, value) {
			this.follower[key] = value;
		},

		handleSelectedEvent(event) {
			this.event = new Event(event);
			this.follower.setEventId(this.event.id);
		},

		handleSelectedTemplate(templateName) {
			this.follower.setTemplateName(templateName);
		},
		handleSelectedLanguage(language) {
			this.follower.setLanguage(language);
		},

		handleSubmit() {
			if (!this.followersConfig.exist) {
				this.openRequiredConfigAlert("warning");
				return;
			}

			const errorProp = this.follower.validate();

			if (!errorProp) {
				this.follower.id ? this.updateScheduleFollower() : this.createScheduleFollower();
				return;
			}

			const targetId = `${errorProp}-settings`;
			const i18nErrorProp = this.$t(`Follower.${errorProp}_error`);

			this.$vs.notify({
				title: this.$t("MissingFieldTitle"),
				text: this.$t(i18nErrorProp),
				color: "warning",
				time: 5000,
			});

			if (errorProp === "template_name") {
				this.templateError = true;
			}

			this.scrollToDiv(targetId);
		},

		handleCancel() {
			// go previous page
			this.$router.go(-1);
		},

		scrollToDiv(targetId) {
			const target = document.getElementById(targetId);
			if (!target) return;
			const targetPosition = target.getBoundingClientRect().top + window.scrollY - 200;
			window.scrollTo({
				top: targetPosition,
				behavior: "smooth",
			});
		},

		async fetchTemplates() {
			try {
				this.openLoading();
				const response = await this.$store.dispatch("moduleSchedule/fetchTemplates");
				if (!response) return;
			} catch (error) {
				console.error(error);
			} finally {
				this.closeLoading();
			}
		},

		async createScheduleFollower() {
			try {
				this.openLoading();
				// Assuming you have an API call to create a follower
				const response = await this.$store.dispatch("moduleSchedule/createScheduleFollower", {
					...this.follower,
				});

				if (response && response.status === 201) {
					this.$vs.notify({
						color: "success",
						title: this.$t("Follower.Followers"),
						text: this.$t("Record.TextConfirmRecordCreate"),
					});

					// go to previous page
					this.$router.go(-1);
				} else {
					this.handleValidationErrors(response.error);
				}
			} catch (error) {
				console.error(error);
			} finally {
				this.closeLoading();
			}
		},

		async updateScheduleFollower() {
			try {
				this.openLoading();
				// Assuming you have an API call to update a follower
				const response = await this.$store.dispatch("moduleSchedule/updateScheduleFollower", {
					...this.follower,
				});

				if (response && response.status === 200) {
					this.$vs.notify({
						color: "success",
						title: this.$t("Follower.Followers"),
						text: this.$t("Record.TextConfirmRecordUpdate"),
					});
					this.$router.go(-1);
				} else {
					this.handleValidationErrors(response.error);
				}
			} catch (error) {
				console.error(error);
				this.$vs.notify({
					color: "error",
					title: this.$t("ErrorTitle"),
					text: this.$t("ErrorMessage"),
				});
			} finally {
				this.closeLoading();
			}
		},

		/**
		 * Fetches the follower by ID.
		 * This dispatches an action to fetch the follower by ID and sets the follower data to `follower`.
		 *
		 * @param {number} followerId is the ID of the follower to fetch.
		 * @returns {Promise<void>} A promise that resolves when the follower data is fetched.
		 */

		async fetchScheduleFollowerById(followerId) {
			try {
				this.openLoading();

				const response = await this.$store.dispatch("moduleSchedule/fetchScheduleFollowerById", followerId);

				if (response && response.status === 201) {
					setTimeout(() => {
						this.follower = new Follower({...response.data.records[0]});
					}, 500);
				}
			} catch (error) {
				console.error(error);
			} finally {
				this.closeLoading();
			}
		},

		/**
		 * Fetches event details by event ID.
		 *
		 * This method constructs a payload with the event ID, date range, and required fields,
		 * then dispatches an action to fetch the event details from the store. If the response
		 * contains event data, it sets the event data to `dataEvent` and fetches the related
		 * visit point details.
		 *
		 * @param {number} eventId - The ID of the event to fetch.
		 * @returns {Promise<void>} - A promise that resolves when the event data is fetched.
		 */
		async fetchEventById(eventId) {
			const payload = {
				id: [+eventId],
				init_date: DateUtils.toDefaultDateTime(this.firstDayOfCurrentYear(), "YYYY-MM-DD"),
				end_date: DateUtils.toDefaultDateTime(this.lastDayOfCurrentYear(), "YYYY-MM-DD"),
			};

			this.openLoading();

			try {
				const response = await this.$store.dispatch("moduleSchedule/fetchEventById", payload);

				if (response && response.length > 0) {
					this.event = new Event(response[0]);
					this.follower.setEventId(this.event.id);
				}
			} catch (error) {
				console.error(error);
			} finally {
				this.closeLoading();
			}
		},

		async fetchCompanyConfigFollower() {
			try {
				this.openLoading();
				const response = await this.$store.dispatch("moduleCompanyConfig/fetchCompanyConfigFollower");
			} catch (error) {
				console.error("fetchCompanyConfigFollower", error);
				this.openRequiredConfigAlert("warning");
			} finally {
				this.closeLoading();
			}
		},

		/**
		 * Processes validation errors and displays notifications for each unique path.
		 * @param {Array} errors - The array of error objects.
		 * @param {Function} notify - The notification function (e.g., this.$vs.notify).
		 */
		handleValidationErrors(errors) {
			const messages = {
				"NOT ASSIGNED already exist.": this.$t("Schedule.NotAssigned"),
				"ASSIGNED already exist.": this.$t("Schedule.Scheduled"),
				"IN PROGRESS already exist.": this.$t("Schedule.InProgress"),
				"COMPLETED already exist.": this.$t("Schedule.Finished"),
				"RESCHEDULED already exist.": this.$t("Schedule.ReProgramming"),
				"CANCELLED already exist.": this.$t("Schedule.Canceled"),
				"DELAYED already exist.": this.$t("Schedule.Delayed"),
				"ON THE WAY already exist.": this.$t("Schedule.OnTheWay"),
			};
			// Group errors by their absolute_path
			const firstError = errors[0];
			const absolutePath = firstError.absolute_path;
			const errorMessages = firstError.message;
			const props = absolutePath[2];
			const message = messages[errorMessages] || "ErrorTitle";

			let payloadValue = "";
			let errorTitle = this.$t("ErrorTitle");
			let errorText = this.$t("ErrorMessage");

			if (props === "cell_phone") {
				payloadValue = `${this.follower.cell_phone[absolutePath[3]]}`;
				errorText =
					this.$t("Follower.alreadyExistToPhoneNumber", {id: message.toUpperCase()}) + ` ${payloadValue}`;
			}

			if (props === "email") {
				payloadValue = ` ${this.follower.email[absolutePath[3]]}`;
				errorText = this.$t("Follower.alreadyExistToEmail", {id: message.toUpperCase()}) + ` ${payloadValue}`;
			}

			this.$vs.notify({
				title: errorTitle,
				text: errorText,
				color: "danger",
				time: 6000,
			});
		},

		firstDayOfCurrentYear() {
			return new Date(this.currentDay.getFullYear(), 0, 1);
		},

		lastDayOfCurrentYear() {
			return new Date(this.currentDay.getFullYear(), 11, 31);
		},

		openRequiredConfigAlert(color) {
			this.$vs.dialog({
				color,
				title: this.$t("CompanySettings"),
				text:
					this.$t("Follower.ConfigCompanyRequired", {
						id: this.$t("Follower.Followers").toUpperCase(),
					}) +
					"." +
					this.$t("Follower.PleaseContactAdmin"),
				acceptText: this.$t("Accept"),
				accept: () => {},
			});
		},
	},

	computed: {
		followersConfig() {
			return this.$store.getters["moduleCompanyConfig/getFollowersConfig"];
		},
		activeAlert() {
			return !this.follower.whatsapp && !this.follower.sms && !this.follower.to_email;
		},

		templateList() {
			return this.$store.getters["moduleSchedule/getTemplates"];
		},
	},

	mounted() {
		const eventId = this.$route.params.event_id;
		const followerId = this.$route.params.follower_id;

		const selectedEvent = this.$store.getters["moduleSchedule/getSelectedEventById"](eventId);

		if (selectedEvent) {
			this.event = new Event(selectedEvent);
			this.follower.setEventId(this.event.id);
		} else {
			if (eventId) this.fetchEventById(eventId);
		}

		if (followerId) {
			this.fetchScheduleFollowerById(Number(followerId));
		}
	},

	created() {
		// component created hook
		if (!moduleSchedule.isRegistered) {
			this.$store.registerModule("moduleSchedule", moduleSchedule);
			moduleSchedule.isRegistered = true;
		}

		if (this.templateList.length === 0) {
			this.fetchTemplates();
		}
	},

	beforeMount() {
		this.fetchCompanyConfigFollower();
	},
};
</script>

<style scoped>
/* component styles */
</style>
